import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from '../components/ui/Card';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Loading } from '../components/ui/Loading';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { TokenBar } from '../components/ui/token-bar';
import { ArrowLeft, Coins, Cpu, DollarSign, FileText, Layers } from 'lucide-react';
import { format } from 'date-fns';
import { formatTokens } from '../utils/format-tokens';
import { calculateModelCost, formatCost } from '../utils/format-cost';
import { useTelemetryCost } from '../hooks/use-telemetry-cost';

const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' },
  labelStyle: { color: '#d1d5db' },
  itemStyle: { color: '#d1d5db' },
};

const Telemetry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showRawJson, setShowRawJson] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['analysis-telemetry', id],
    queryFn: () => adminApiClient.getAnalysisTelemetry(id!),
    enabled: !!id,
    retry: false,
  });

  const { totalCost, costByProvider, hasCostData, findPricing } =
    useTelemetryCost(data?.token_usage.by_model_detailed ?? null);

  const is404 = (error as any)?.response?.status === 404;

  if (isLoading) return <Loading message="Loading telemetry..." />;

  if (is404 || (error && !data)) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/admin/analyses')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Analyses
        </Button>
        <div className="text-center py-16">
          <p className="text-gray-400 text-lg">
            {is404 ? 'Telemetry not found or expired.' : 'Failed to load telemetry.'}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { token_usage } = data;

  // Chart data transforms
  const byModelChartData = token_usage.by_model_detailed
    ? token_usage.by_model_detailed.map((m) => ({
        name: m.model,
        input: m.input_tokens,
        output: m.output_tokens,
      }))
    : Object.entries(token_usage.by_model).map(([name, value]) => ({
        name,
        input: value,
        output: 0,
      }));

  const byStageChartData = token_usage.by_stage_detailed
    ? token_usage.by_stage_detailed.map((s) => ({
        name: s.stage,
        input: s.input_tokens,
        output: s.output_tokens,
      }))
    : token_usage.by_stage
      ? Object.entries(token_usage.by_stage).map(([name, value]) => ({
          name,
          input: value,
          output: 0,
        }))
      : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate('/admin/analyses')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">Analysis Telemetry</h1>
          <p className="text-gray-400 text-sm font-mono mt-1">{data.analysis_id}</p>
          <p className="text-gray-500 text-xs mt-1">
            {format(new Date(data.timestamp), 'MMM d, yyyy HH:mm:ss')}
            {data.job_id && <span className="ml-3">Job: {data.job_id}</span>}
          </p>
        </div>
      </div>

      {/* Summary Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Coins} title="Total Tokens" value={formatTokens(token_usage.total_tokens)} />
        <StatCard
          icon={Layers}
          title="Input / Output"
          value={`${formatTokens(token_usage.input_tokens)} / ${formatTokens(token_usage.output_tokens)}`}
        />
        <StatCard icon={Cpu} title="LLM Calls" value={token_usage.llm_call_count.toString()} />
        <StatCard
          icon={DollarSign}
          title="Estimated Cost"
          value={hasCostData ? formatCost(totalCost) : '—'}
          subtitle={hasCostData ? 'raw provider cost' : 'pricing unavailable'}
        />
        <StatCard
          icon={FileText}
          title="Lines Analyzed"
          value={token_usage.total_lines_analyzed != null ? token_usage.total_lines_analyzed.toLocaleString() : '—'}
          subtitle={token_usage.tokens_per_line != null ? `${token_usage.tokens_per_line.toFixed(1)} tok/line` : undefined}
        />
      </div>

      {/* Token Distribution */}
      <Card>
        <CardTitle className="mb-4">Token Distribution</CardTitle>
        <div className="space-y-3">
          <TokenBar label="Input" value={token_usage.input_tokens} total={token_usage.total_tokens} color="bg-blue-500" />
          <TokenBar label="Output" value={token_usage.output_tokens} total={token_usage.total_tokens} color="bg-green-500" />
          {(token_usage.cache_creation_tokens > 0 || token_usage.cache_read_tokens > 0) && (
            <div className="pt-3 border-t border-gray-700 flex space-x-6 text-sm text-gray-400">
              <span>Cache created: <span className="text-white">{formatTokens(token_usage.cache_creation_tokens)}</span></span>
              <span>Cache read: <span className="text-white">{formatTokens(token_usage.cache_read_tokens)}</span></span>
              {token_usage.input_cached_tokens != null && (
                <span>Input cached: <span className="text-white">{formatTokens(token_usage.input_cached_tokens)}</span></span>
              )}
              {token_usage.output_cached_tokens != null && (
                <span>Output cached: <span className="text-white">{formatTokens(token_usage.output_cached_tokens)}</span></span>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* By Model Section */}
      {byModelChartData.length > 0 && (
        <Card>
          <CardTitle className="mb-4">By Model</CardTitle>
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byModelChartData}>
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatTokens} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => formatTokens(v)} />
                <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                <Bar dataKey="input" name="Input" fill="#3b82f6" stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="output" name="Output" fill="#22c55e" stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {token_usage.by_model_detailed && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Model</TableCell>
                  <TableCell header>Provider</TableCell>
                  <TableCell header>Input</TableCell>
                  <TableCell header>Output</TableCell>
                  <TableCell header>Total</TableCell>
                  <TableCell header>Cost</TableCell>
                  <TableCell header>Calls</TableCell>
                  <TableCell header>Cached In</TableCell>
                  <TableCell header>Cached Out</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {token_usage.by_model_detailed.map((m, i) => {
                  const pricing = findPricing(m.model);
                  const cost = pricing
                    ? calculateModelCost(m.input_tokens, m.output_tokens, m.input_cached_tokens, pricing)
                    : null;
                  return (
                    <TableRow key={i}>
                      <TableCell><span className="text-gray-300 text-xs font-mono">{m.model}</span></TableCell>
                      <TableCell><span className="text-gray-400 text-xs">{m.provider}</span></TableCell>
                      <TableCell><span className="text-blue-400 text-sm">{formatTokens(m.input_tokens)}</span></TableCell>
                      <TableCell><span className="text-green-400 text-sm">{formatTokens(m.output_tokens)}</span></TableCell>
                      <TableCell><span className="text-white text-sm">{formatTokens(m.total_tokens)}</span></TableCell>
                      <TableCell>
                        <span className="text-yellow-400 text-sm">{cost != null ? formatCost(cost) : '—'}</span>
                      </TableCell>
                      <TableCell><span className="text-gray-300 text-sm">{m.call_count}</span></TableCell>
                      <TableCell><span className="text-gray-400 text-sm">{m.input_cached_tokens != null ? formatTokens(m.input_cached_tokens) : '—'}</span></TableCell>
                      <TableCell><span className="text-gray-400 text-sm">{m.output_cached_tokens != null ? formatTokens(m.output_cached_tokens) : '—'}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Per-provider cost summary */}
          {hasCostData && Object.keys(costByProvider).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {Object.entries(costByProvider)
                .sort(([, a], [, b]) => b - a)
                .map(([provider, cost]) => (
                  <div key={provider} className="px-3 py-2 bg-gray-900 rounded-lg text-sm">
                    <span className="text-gray-400">{provider}:</span>{' '}
                    <span className="text-yellow-400 font-medium">{formatCost(cost)}</span>
                  </div>
                ))}
              <div className="px-3 py-2 bg-gray-800 rounded-lg text-sm border border-gray-700">
                <span className="text-gray-400">Total:</span>{' '}
                <span className="text-yellow-300 font-semibold">{formatCost(totalCost)}</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* By Stage Section */}
      {byStageChartData.length > 0 && (
        <Card>
          <CardTitle className="mb-4">By Stage</CardTitle>
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byStageChartData}>
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatTokens} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => formatTokens(v)} />
                <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                <Bar dataKey="input" name="Input" fill="#3b82f6" stackId="a" />
                <Bar dataKey="output" name="Output" fill="#22c55e" stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {token_usage.by_stage_detailed && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Stage</TableCell>
                  <TableCell header>Input</TableCell>
                  <TableCell header>Output</TableCell>
                  <TableCell header>Total</TableCell>
                  <TableCell header>Calls</TableCell>
                  <TableCell header>Lines</TableCell>
                  <TableCell header>Tok/Line</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {token_usage.by_stage_detailed.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell><span className="text-gray-300 text-sm">{s.stage}</span></TableCell>
                    <TableCell><span className="text-blue-400 text-sm">{formatTokens(s.input_tokens)}</span></TableCell>
                    <TableCell><span className="text-green-400 text-sm">{formatTokens(s.output_tokens)}</span></TableCell>
                    <TableCell><span className="text-white text-sm">{formatTokens(s.total_tokens)}</span></TableCell>
                    <TableCell><span className="text-gray-300 text-sm">{s.call_count}</span></TableCell>
                    <TableCell><span className="text-gray-400 text-sm">{s.lines_analyzed != null ? s.lines_analyzed.toLocaleString() : '—'}</span></TableCell>
                    <TableCell><span className="text-gray-400 text-sm">{s.tokens_per_line != null ? s.tokens_per_line.toFixed(1) : '—'}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* Raw JSON */}
      <Card>
        <button
          onClick={() => setShowRawJson(!showRawJson)}
          className="w-full text-left text-sm text-gray-400 hover:text-gray-300"
        >
          {showRawJson ? '▼' : '▶'} Raw JSON
        </button>
        {showRawJson && (
          <pre className="mt-3 p-4 bg-gray-900 rounded-lg text-xs text-gray-300 overflow-auto max-h-96 font-mono">
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </Card>
    </div>
  );
};

export default Telemetry;
